<template>
    <div class="interests-page">
        <v-container class="fill-height d-flex align-center justify-center">
            <v-card flat class="interests-card" max-width="1000">
                <!-- Progress indicator -->
                <div v-if="currentStep < 4" class="step-indicator mb-6">
                    {{ currentStep }} / {{ totalSteps }}
                </div>

                <!-- Main title and subtitle -->
                <h1 v-if="currentStep < 4" class="main-title mb-3">Help Us Personalize Your Learning Experience</h1>
                <p v-if="currentStep < 4" class="main-subtitle mb-10">
                    Your answers will help us match you with the best course, teacher and plan.
                </p>

                <!-- Step 1: Learning Goals -->
                <div v-if="currentStep === 1" class="step-content">
                    <div class="content-box">
                        <h2 class="step-title mb-8">Learning goals</h2>
                        
                        <div class="options-grid">
                            <div
                                v-for="goal in learningGoals"
                                :key="goal.id"
                                class="option-item"
                                :class="{ 'selected': selectedGoals.includes(goal.id) }"
                                @click="toggleGoal(goal.id)"
                            >
                                <div class="option-content">
                                    <img 
                                        :src="getGoalIcon(goal.icon)" 
                                        :alt="goal.name"
                                        class="goal-icon"
                                        :class="{ 'selected': selectedGoals.includes(goal.id) }"
                                    />
                                    <span class="option-text">{{ goal.name }}</span>
                                </div>
                                <v-icon v-if="selectedGoals.includes(goal.id)" color="#D4780A" size="24">
                                    mdi-check-circle
                                </v-icon>
                            </div>
                        </div>

                        <v-btn
                            block
                            x-large
                            depressed
                            color="#D4780A"
                            class="continue-btn white--text mt-10"
                            :disabled="selectedGoals.length === 0"
                            @click="nextStep"
                        >
                            Continue
                        </v-btn>
                    </div>
                </div>

                <!-- Step 2: Skill Level -->
                <div v-if="currentStep === 2" class="step-content">
                    <div class="content-box">
                        <h2 class="step-title mb-8">Skill level</h2>
                        
                        <div class="options-list">
                        <div
                            class="option-item"
                            :class="{ 'selected': skillLevel === 'beginner' }"
                            @click="skillLevel = 'beginner'"
                        >
                            <div class="option-content">
                                <img 
                                    src="/img/skills/beginner.svg" 
                                    alt="Beginner"
                                    class="option-icon"
                                    :class="{ 'selected': skillLevel === 'beginner' }"
                                />
                                <span class="option-text">Beginner</span>
                            </div>
                            <v-icon v-if="skillLevel === 'beginner'" color="#D4780A" size="24">
                                mdi-check-circle
                            </v-icon>
                        </div>

                        <div
                            class="option-item"
                            :class="{ 'selected': skillLevel === 'intermediate' }"
                            @click="skillLevel = 'intermediate'"
                        >
                            <div class="option-content">
                                <img 
                                    src="/img/skills/intermediate.svg" 
                                    alt="Intermediate"
                                    class="option-icon"
                                    :class="{ 'selected': skillLevel === 'intermediate' }"
                                />
                                <span class="option-text">Intermediate</span>
                            </div>
                            <v-icon v-if="skillLevel === 'intermediate'" color="#D4780A" size="24">
                                mdi-check-circle
                            </v-icon>
                        </div>

                        <div
                            class="option-item"
                            :class="{ 'selected': skillLevel === 'advanced' }"
                            @click="skillLevel = 'advanced'"
                        >
                            <div class="option-content">
                                <img 
                                    src="/img/skills/advanced.svg" 
                                    alt="Advanced"
                                    class="option-icon"
                                    :class="{ 'selected': skillLevel === 'advanced' }"
                                />
                                <span class="option-text">Advanced</span>
                            </div>
                            <v-icon v-if="skillLevel === 'advanced'" color="#D4780A" size="24">
                                mdi-check-circle
                            </v-icon>
                        </div>
                    </div>

                        <div class="button-group mt-10">
                            <v-btn
                                block
                                outlined
                                x-large
                                depressed
                                color="#D4780A"
                                class="back-btn"
                                @click="previousStep"
                            >
                                Back
                            </v-btn>
                            <v-btn
                                block
                                x-large
                                depressed
                                color="#D4780A"
                                class="continue-btn white--text"
                                :disabled="!skillLevel"
                                @click="nextStep"
                            >
                                Continue
                            </v-btn>
                        </div>
                    </div>
                </div>

                <!-- Step 3: Age Range -->
                <div v-if="currentStep === 3" class="step-content">
                    <div class="content-box">
                        <h2 class="step-title mb-8">Age</h2>
                        
                        <div class="options-list">
                        <div
                            class="option-item"
                            :class="{ 'selected': ageRange === 'under_12' }"
                            @click="ageRange = 'under_12'"
                        >
                            <div class="option-content">
                                <img 
                                    src="/img/age/under_12.svg" 
                                    alt="Under 12 years"
                                    class="option-icon"
                                    :class="{ 'selected': ageRange === 'under_12' }"
                                />
                                <span class="option-text">Under 12 years</span>
                            </div>
                            <v-icon v-if="ageRange === 'under_12'" color="#D4780A" size="24">
                                mdi-check-circle
                            </v-icon>
                        </div>

                        <div
                            class="option-item"
                            :class="{ 'selected': ageRange === '12_17' }"
                            @click="ageRange = '12_17'"
                        >
                            <div class="option-content">
                                <img 
                                    src="/img/age/12_17.svg" 
                                    alt="12–17 years"
                                    class="option-icon"
                                    :class="{ 'selected': ageRange === '12_17' }"
                                />
                                <span class="option-text">12–17 years</span>
                            </div>
                            <v-icon v-if="ageRange === '12_17'" color="#D4780A" size="24">
                                mdi-check-circle
                            </v-icon>
                        </div>

                        <div
                            class="option-item"
                            :class="{ 'selected': ageRange === '18_30' }"
                            @click="ageRange = '18_30'"
                        >
                            <div class="option-content">
                                <img 
                                    src="/img/age/18_30.svg" 
                                    alt="18–30 years"
                                    class="option-icon"
                                    :class="{ 'selected': ageRange === '18_30' }"
                                />
                                <span class="option-text">18–30 years</span>
                            </div>
                            <v-icon v-if="ageRange === '18_30'" color="#D4780A" size="24">
                                mdi-check-circle
                            </v-icon>
                        </div>

                        <div
                            class="option-item"
                            :class="{ 'selected': ageRange === '30_plus' }"
                            @click="ageRange = '30_plus'"
                        >
                            <div class="option-content">
                                <img 
                                    src="/img/age/30_plus.svg" 
                                    alt="30+ years"
                                    class="option-icon"
                                    :class="{ 'selected': ageRange === '30_plus' }"
                                />
                                <span class="option-text">30+ years</span>
                            </div>
                            <v-icon v-if="ageRange === '30_plus'" color="#D4780A" size="24">
                                mdi-check-circle
                            </v-icon>
                        </div>
                        </div>

                        <div class="button-group mt-10">
                            <v-btn
                                block
                                outlined
                                x-large
                                depressed
                                color="#D4780A"
                                class="back-btn"
                                @click="previousStep"
                            >
                                Back
                            </v-btn>
                            <v-btn
                                block
                                x-large
                                depressed
                                color="#D4780A"
                                class="continue-btn white--text"
                                :disabled="!ageRange"
                                @click="nextStep"
                            >
                                Continue
                            </v-btn>
                        </div>
                    </div>
                </div>

                <!-- Step 4: Success -->
                <div v-if="currentStep === 4" class="step-content success-step">
                    <div class="success-icon-wrapper mb-10">
                        <img src="/img/success-badge.svg" alt="Success" class="success-badge" />
                    </div>
                    
                    <h2 class="success-title mb-4">Interests submitted successfully!</h2>
                    <p class="success-subtitle mb-12">
                        Welcome aboard! Start your success journey with us!
                    </p>

                    <v-btn
                        depressed
                        x-large
                        color="#D4780A"
                        class="start-journey-btn white--text"
                        @click="startJourney"
                        :loading="saving"
                    >
                        Start My Journey
                    </v-btn>
                </div>
            </v-card>
        </v-container>
    </div>
</template>

<script>
export default {
    name: 'Interests',
    data() {
        return {
            currentStep: 1,
            totalSteps: 4,
            learningGoals: [],
            selectedGoals: [],
            skillLevel: '',
            ageRange: '',
            saving: false,
        };
    },
    computed: {
        user() {
            return this.$store.state.userModule.user;
        },
    },
    methods: {
        async fetchLearningGoals() {
            try {
                const response = await window.axios.get('/interests/goals');
                this.learningGoals = response.data;
            } catch (error) {
                console.error('Error fetching learning goals:', error);
                this.showMsg('Error loading learning goals', 'error');
            }
        },
        toggleGoal(goalId) {
            const index = this.selectedGoals.indexOf(goalId);
            if (index > -1) {
                this.selectedGoals.splice(index, 1);
            } else {
                this.selectedGoals.push(goalId);
            }
        },
        getGoalIcon(iconName) {
            // Return SVG path from public folder or assets
            // You can place SVG files in public/images/goals/ folder
            return `/img/goals/${iconName}.svg`;
        },
        previousStep() {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
        },
        nextStep() {
            if (this.currentStep < 3) {
                this.currentStep++;
            } else if (this.currentStep === 3) {
                // Save to backend and show success
                this.saveInterests();
            }
        },
        async saveInterests() {
            this.saving = true;
            try {
                await this.$store.dispatch('saveInterests', {
                    learning_goals: this.selectedGoals,
                    skill_level: this.skillLevel,
                    age_range: this.ageRange,
                });
                this.currentStep = 4;
            } catch (error) {
                console.error('Error saving interests:', error);
                this.showMsg('Error saving your preferences', 'error');
            } finally {
                this.saving = false;
            }
        },
        startJourney() {
            this.$router.push({ name: 'courses' });
        },
        showMsg(text, color = 'error', timeout = 4000) {
            this.$store.dispatch('addSnackMsg', {
                show: true,
                text: text,
                color: color,
                timeout: timeout,
                msgBar: true
            });
        },
    },
    mounted() {
        // Redirect if already completed
        if (this.user.interests_completed) {
            this.$router.push({ name: 'home' });
            return;
        }
        
        this.fetchLearningGoals();
    },
};
</script>

<style lang="scss" scoped>
.interests-page {
    min-height: 100vh;
    background-color: #FAFAFA;
    @media (max-width: 960px) {
        padding: 0 !important;
        background-color: white !important;
    }
}

.interests-card {
    width: 100%;
    padding: 60px 40px;
    background: white;
    border-radius: 24px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    text-align: center;

    @media (max-width: 960px) {
        padding: 32px 16px 80px 16px !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        background-color: transparent !important;
    }
}

.step-indicator {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #9E9E9E;
    font-weight: 500;
}

.main-title {
    font-family: 'Inter', sans-serif;
    font-size: 32px;
    font-weight: 700;
    color: #1E1E1E;
    line-height: 1.3;

    @media (max-width: 600px) {
        font-size: 26px; /* Increased from 24px */
        margin-bottom: 24px !important;
    }
}

.main-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    color: #707070;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
    
    @media (max-width: 600px) {
        font-size: 15px; /* Added explicit mobile size */
        margin-bottom: 32px !important;
    }
}

.step-content {
    max-width: 720px;
    margin: 0 auto;
}

.content-box {
    background: white;
    border-radius: 24px;
    padding: 48px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid #E8E8E8;

    @media (max-width: 960px) {
        padding: 0 !important;
        box-shadow: none !important;
        border: none !important;
        background-color: transparent !important;
    }
}

.step-title {
    font-family: 'Inter', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1E1E1E;
    
    @media (max-width: 600px) {
        font-size: 24px;
        margin-bottom: 24px !important;
    }
}

.options-grid {
    display: flex;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    flex-direction: column;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
}

.options-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.option-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border: 2px solid #E0E0E0;
    border-radius: 16px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: #D4780A;
    }

    @media (max-width: 600px) {
        padding: 18px 20px;
        border-width: 1px;
    }

    &.selected {
        border-color: #D4780A;
        background: #FFF8F0;
    }
}

.option-content {
    display: flex;
    align-items: center;
    gap: 16px;
}

.goal-icon,
.option-icon {
    width: 32px;
    height: 32px;
    object-fit: contain;
    transition: all 0.2s ease;
    
    &.selected {
        filter: brightness(0) saturate(100%) invert(44%) sepia(93%) saturate(1028%) hue-rotate(360deg) brightness(94%) contrast(92%);
    }

    @media (max-width: 600px) {
        width: 36px;
        height: 36px;
    }
}

.option-text {
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #1E1E1E;

    @media (max-width: 600px) {
        font-size: 16px;
    }
}

.button-group {
    display: flex;
    gap: 16px;

    @media (max-width: 960px) {
        flex-direction: column-reverse;
        gap: 12px;
        width: 100%;
        align-items: stretch;
    }
}

.back-btn {
    flex: 1;
    height: 56px !important;
    border-radius: 12px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: none;
    border-width: 2px !important;

    @media (max-width: 960px) {
        width: 100% !important;
        flex: none;
    }
}

.continue-btn {
    flex: 2;
    height: 56px !important;
    border-radius: 12px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: none;
    
    @media (max-width: 960px) {
        width: 100% !important;
        flex: none;
        margin-bottom: 0px; 
    }
}

.success-step {
    text-align: center;
    padding-top: 40px;
    
    @media (max-width: 600px) {
        padding-top: 20px;
    }
}

.success-icon-wrapper {
    display: flex;
    justify-content: center;
}

.success-badge {
    width: 160px;
    height: 160px;
    object-fit: contain;
    
    @media (max-width: 600px) {
        width: 120px;
        height: 120px;
    }
}

.success-title {
    font-family: 'Inter', sans-serif;
    font-size: 32px;
    font-weight: 700;
    color: #1E1E1E;
    line-height: 1.2;
    
    @media (max-width: 600px) {
        font-size: 28px;
    }
}

.success-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    color: #9E9E9E;
    line-height: 1.6;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
}

.start-journey-btn {
    height: 56px !important;
    border-radius: 12px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: none;
    padding: 0 48px !important;
    
    @media (max-width: 600px) {
        width: 100%;
        max-width: 340px;
    }
}
</style>

